/**
 * Onboarding Route Wrapper Component
 * 
 * Wraps onboarding routes to handle invitation context detection
 * Ensures onboarding pages work independently of authentication tokens
 * 
 * Logic:
 * - If URL has invitation context (token, tempToken, etc.) → Always show onboarding page
 * - If no invitation context AND authenticated → Can redirect (handled by individual components)
 * - If no invitation context AND not authenticated → Show onboarding page (for direct access)
 * 
 * Usage:
 * <OnboardingRouteWrapper>
 *   <OnboardingDashboard />
 * </OnboardingRouteWrapper>
 */

import React from 'react'
import { useSearchParams } from 'react-router-dom'
import { hasInvitationContext } from '@/utils/onboarding-context.util'

interface OnboardingRouteWrapperProps {
  children: React.ReactElement<{
    isInvitationFlow?: boolean
    hasInvitationContext?: boolean
  }>
}

export function OnboardingRouteWrapper({ children }: OnboardingRouteWrapperProps) {
  const [searchParams] = useSearchParams()
  
  // Check if URL has invitation context
  const hasInvitation = hasInvitationContext(searchParams)
  
  // If has invitation context, always render children (ignore auth state)
  // Individual components can still check auth for their own logic,
  // but they won't redirect away from onboarding flow
  if (hasInvitation) {
    // Pass a prop to children indicating they're in invitation flow
    return React.cloneElement(children, { 
      isInvitationFlow: true,
      hasInvitationContext: true,
    })
  }
  
  // No invitation context - render normally
  // Component can decide what to do (check auth, redirect, etc.)
  return children
}

export default OnboardingRouteWrapper

